import { useContext, useMemo, type ReactNode } from "react";
import {
  can,
  canAt,
  canOrganizationWide,
  facilityScopeFor,
  withFrontDeskReach,
  type FacilityIds,
  type PolicySubject,
} from "@alm/policy";
import type { Me } from "@alm/api-contracts";
import { chosenFacility } from "@/lib/facility/facility-choice";
import {
  policyAt,
  PolicyContext,
  type PolicyValue,
  usePolicy,
  navigationFor,
} from "@/lib/policy/policy-shared";
import type { Permission, FeatureKey, NavItem } from "@alm/policy";

export { navigationFor, usePolicy, type PolicyValue } from "@/lib/policy/policy-shared";
export { PolicyScope } from "@/lib/policy/policy-scope";

// The API's own `can()` on the subject it handed us. Hides and disables; never protects.
export function PolicyProvider({ me, children }: { me: Me; children: ReactNode }) {
  const value = useMemo(() => policyAt(me, me.subject as PolicySubject, null), [me]);
  return <PolicyContext.Provider value={value}>{children}</PolicyContext.Provider>;
}

// Only the shift board uses this: a tablet paired to a building has no session or subject.
// Everything else should throw via `usePolicy`.
export function useOptionalPolicy(): PolicyValue | null {
  return useContext(PolicyContext);
}

// Leave the building out for the one in view; name the record's building for a record's actions.
export function useCan(permission: Permission, facilityId?: string | null): boolean {
  return usePolicy().can(permission, facilityId);
}

// For a door whose request names no building (catalog, course, template, account): the API answers
// on the organization role or a role at any building, not a building administrator's set.
export function useCanWithoutBuilding(permission: Permission): boolean {
  return usePolicy().can(permission, null);
}

// For controls not about one building, where a building-only grant (front desk) must count wherever
// the person looks, and for people doors opened to a building administrator anywhere
// (`requirePermissionOrFacilityAdmin`); the service then decides per person.
export function useCanAnywhere(permission: Permission): boolean {
  return canAt(usePolicy().subject, permission, null);
}

// A building role never opens the organization's own doors (deactivating, deleting, a doctor's
// login).
export function useCanOrganizationWide(permission: Permission): boolean {
  return canOrganizationWide(usePolicy().subject, permission);
}

// As the API's `assertRowAccess`: a building's row needs the permission there; a shared row needs
// it organization-wide.
export function useCanChangeRow(permission: Permission): (facilityId: string | null) => boolean {
  const { subject } = usePolicy();
  return (facilityId) =>
    facilityId
      ? can(subject, permission, { facilityId })
      : canOrganizationWide(subject, permission);
}

export function useScopeFor(permission: Permission): FacilityIds {
  return facilityScopeFor(usePolicy().subject, permission);
}

export function useOnlyFrontDeskBuilding(permission: Permission): string | null {
  const scope = facilityScopeFor(withFrontDeskReach(usePolicy().subject), permission);
  return scope?.length === 1 ? (scope[0] ?? null) : null;
}

// As `assertNewRowBuilding`.
export function useBuildingsWhere<T extends { id: string }>(
  permission: Permission,
  facilities: readonly T[],
): T[] {
  const { subject } = usePolicy();
  return facilities.filter((facility) => can(subject, permission, { facilityId: facility.id }));
}

export function useFeature(key: FeatureKey, facilityId?: string | null): boolean {
  return usePolicy().feature(key, facilityId);
}

export function routeAllowed(me: Me, entry: NavItem): boolean {
  const subject = me.subject as PolicySubject;
  return navigationFor(subject, chosenFacility(subject)).some((item) => item.id === entry.id);
}
